// src/features/admin/roleMasterApi.ts - COMPLETE FIX
import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { adminRefreshToken } from '../../features/admin/adminApi.ts';

// Types for Role Master
export interface Role {
  id: number;
  role_name: string;
  role_desc: string | null;
  createddt: string;
  updateddt: string | null;
}

export interface AddRoleRequest {
  role_name: string;
  role_desc: string;
}

export interface AddRoleResponse {
  error: boolean;
  message: string;
  recId?: number;
}

export interface ListRolesResponse {
  error: boolean;
  message: string;
  roles?: Role[];
}

export interface UpdateRoleRequest {
  id: number;
  role_name: string;
  role_desc: string;
}

export interface UpdateRoleResponse {
  error: boolean;
  message: string;
}

export interface DeleteRoleResponse {
  error: boolean;
  message: string;
}

const BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT;
const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';

// Custom base query with token refresh
const baseQueryWithRefresh: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // Get current token
  let token = localStorage.getItem('admin_token');
  
  // If no token, try to refresh
  if (!token) {
    console.log('🔍 No token found, attempting refresh...');
    token = await adminRefreshToken();
  }
  
  // Prepare headers
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  headers.set('authkey', AUTH_KEY);
  
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Check if args is string (URL) or FetchArgs object
  const fetchArgs: FetchArgs = typeof args === 'string' ? { url: args, headers } : { ...args, headers };
  
  // Make the request
  const result = await fetchBaseQuery({ 
    baseUrl: BASE_URL,
    prepareHeaders: () => headers,
  })(fetchArgs, api, extraOptions);
  
  // If request failed with 401 (Unauthorized), try to refresh token and retry
  if (result.error && result.error.status === 401) {
    console.log('🔐 Token expired, attempting refresh...');
    const newToken = await adminRefreshToken();
    
    if (newToken) {
      // Update headers with new token
      headers.set('Authorization', `Bearer ${newToken}`);
      
      // Retry the request
      const retryArgs: FetchArgs = typeof args === 'string' 
        ? { url: args, headers } 
        : { ...args, headers };
      
      return await fetchBaseQuery({ 
        baseUrl: BASE_URL,
        prepareHeaders: () => headers,
      })(retryArgs, api, extraOptions);
    }
  }
  
  return result;
};

export const roleMasterApi = createApi({
  reducerPath: 'roleMasterApi',
  baseQuery: baseQueryWithRefresh,
  tagTypes: ['Roles'],
  endpoints: (build) => ({
    // Add Role
    addRole: build.mutation<AddRoleResponse, AddRoleRequest>({
      query: (data) => {
        console.log('➕ Add Role Data:', data);
        
        return {
          url: '/admin/add_role',
          method: 'POST',
          body: JSON.stringify({
            role_name: data.role_name.trim(),
            role_desc: data.role_desc.trim(),
          }),
        };
      },
      invalidatesTags: ['Roles'],
      transformResponse: (response: any) => {
        console.log('✅ Add Role Response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Role added successfully',
          recId: response.recId,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('❌ Add Role Error:', response);
        
        if (response.status === 409) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'Role already exists' 
            }
          };
        } else if (response.status === 400) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: response.data?.message || 'Invalid data provided' 
            }
          };
        }
        
        return {
          status: response.status,
          data: response.data || { 
            error: true, 
            message: 'Failed to add role. Please try again.' 
          }
        };
      },
    }),

    // List Roles
    listRoles: build.query<ListRolesResponse, void>({
      query: () => {
        console.log('📋 Fetching Roles List');
        
        return {
          url: '/admin/list_roles',
          method: 'POST',
        };
      },
      providesTags: ['Roles'],
      transformResponse: (response: any) => {
        console.log('✅ List Roles Response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Role List',
          roles: response.roles || [],
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('❌ List Roles Error:', response);
        return {
          status: response.status,
          data: response.data || { 
            error: true, 
            message: 'Failed to fetch roles. Please try again.' 
          }
        };
      },
    }),

    // Update Role
    updateRole: build.mutation<UpdateRoleResponse, UpdateRoleRequest>({
      query: (data) => {
        console.log('🔄 Update Role Data:', data);
        
        return {
          url: '/admin/update_role',
          method: 'POST',
          body: JSON.stringify({
            id: data.id,
            role_name: data.role_name.trim(),
            role_desc: data.role_desc.trim(),
          }),
        };
      },
      invalidatesTags: ['Roles'],
      transformResponse: (response: any) => {
        console.log('✅ Update Role Response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Role updated successfully',
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('❌ Update Role Error:', response);
        
        if (response.status === 404) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'Role not found' 
            }
          };
        }
        
        return {
          status: response.status,
          data: response.data || { 
            error: true, 
            message: 'Failed to update role. Please try again.' 
          }
        };
      },
    }),

    // Delete Role
    deleteRole: build.mutation<DeleteRoleResponse, number>({
      query: (id) => {
        console.log('🗑️ Delete Role ID:', id);
        
        return {
          url: '/admin/delete_role',
          method: 'POST',
          body: JSON.stringify({
            id: id,
          }),
        };
      },
      invalidatesTags: ['Roles'],
      transformResponse: (response: any) => {
        console.log('✅ Delete Role Response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Role deleted successfully',
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('❌ Delete Role Error:', response);
        
        if (response.status === 404) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'Role not found' 
            }
          };
        }
        
        return {
          status: response.status,
          data: response.data || { 
            error: true, 
            message: 'Failed to delete role. Please try again.' 
          }
        };
      },
    }),
  }),
});

export const {
  useAddRoleMutation,
  useListRolesQuery,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = roleMasterApi;